/**
 * Recently Viewed Products
 * Uses cookies to store and display recently viewed products with Swiper
 */

class RecentlyViewedProducts {
  constructor() {
    this.cookieName = 'recently_viewed_products';
    this.maxProducts = 4;
    this.init();
  }

  init() {
    // Auto-track current product if on product page
    this.trackCurrentProduct();

    // Load recently viewed products on page load
    this.loadRecentlyViewed();
  }

  // Cookie helper functions
  setCookie(name, value, days = 30) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/';
  }

  getCookie(name) {
    return document.cookie.split('; ').reduce((r, v) => {
      const parts = v.split('=');
      return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, '');
  }

  // Get recently viewed products from cookie
  getRecentlyViewed() {
    const cookie = this.getCookie(this.cookieName);
    return cookie ? JSON.parse(cookie) : [];
  }

  // Save recently viewed products to cookie
  saveRecentlyViewed(products) {
    this.setCookie(this.cookieName, JSON.stringify(products));
  }

  // Add product to recently viewed
  addProduct(productHandle) {
    let products = this.getRecentlyViewed();
    products = products.filter((handle) => handle !== productHandle); // Remove if exists
    products.unshift(productHandle); // Add to beginning
    products = products.slice(0, this.maxProducts); // Keep only max products
    this.saveRecentlyViewed(products);
  }

  // Clear recently viewed products
  clearRecentlyViewed() {
    this.setCookie(this.cookieName, '', -1); // Delete cookie

    // Hide all recently viewed sections
    document.querySelectorAll('[id^="recently-viewed-"]').forEach((section) => {
      section.style.display = 'none';
    });
  }

  // Track current product if on product page
  trackCurrentProduct() {
    // Get product handle from URL or meta tag
    const productHandle = this.getCurrentProductHandle();
    if (productHandle) {
      this.addProduct(productHandle);
    }
  }

  // Get current product handle
  getCurrentProductHandle() {
    // Try to get from URL first
    const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }

    // Try to get from meta tag
    const metaTag = document.querySelector('meta[name="product-handle"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    return null;
  }

  // Load and display recently viewed products
  async loadRecentlyViewed() {
    const productHandles = this.getRecentlyViewed();

    if (productHandles.length === 0) {
      return;
    }

    // Find all recently viewed sections
    const sections = document.querySelectorAll('[id^="recently-viewed-"]');

    sections.forEach(async (section) => {
      const sectionId = section.id.replace('recently-viewed-', '');
      const container = section.querySelector(`#recently-viewed-products-${sectionId}`);

      if (!container) return;

      // Show skeleton loading
      const skeletonCount = Math.min(productHandles.length, 4); // Max 4 skeletons
      const skeletons = Array(skeletonCount)
        .fill(0)
        .map(() => this.createSkeleton())
        .join('');
      container.innerHTML = skeletons;
      section.style.display = 'block';

      try {
        // Load products using product card view
        const promises = productHandles.map(async (handle) => {
          const response = await fetch(`/products/${handle}?view=card`);
          if (response.ok) {
            const html = await response.text();
            return this.extractProductCard(html);
          }
          return null;
        });

        const results = await Promise.all(promises);
        const validProducts = results.filter((html) => html !== null);

        if (validProducts.length > 0) {
          // Replace skeleton with actual products
          container.innerHTML = validProducts.join('');
        } else {
          // Hide section if no products loaded
          section.style.display = 'none';
        }
      } catch (error) {
        console.warn('Error loading recently viewed products:', error);
        // Hide section on error
        section.style.display = 'none';
      }
    });
  }

  // Create skeleton loading HTML
  createSkeleton() {
    return `
      <div class="wp-w-full wp-animate-pulse">
        <div class="relative product_image">
          <div class="wp-rounded-default wp-overflow-hidden wp-bg-gray-200 wp-aspect-square wp-mb-3"></div>
          <div class="wp-flex wp-items-center wp-space-x-1 wp-text-sm wp-mt-1.5">
            <div class="wp-flex wp-space-x-1">
              <div class="wp-w-4 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
              <div class="wp-w-4 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
              <div class="wp-w-4 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
              <div class="wp-w-4 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
              <div class="wp-w-4 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
            </div>
            <div class="wp-w-8 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
            <div class="wp-w-8 wp-h-4 wp-bg-gray-200 wp-rounded"></div>
          </div>
          <div class="wp-h-4 wp-bg-gray-200 wp-rounded wp-mt-1.5 wp-w-3/4"></div>
          <div class="wp-h-5 wp-bg-gray-200 wp-rounded wp-mt-1 wp-w-1/2"></div>
          <div class="wp-h-5 wp-bg-gray-200 wp-rounded wp-mt-2 wp-w-full"></div>
        </div>
      </div>
    `;
  }

  // Extract product card from full page HTML
  extractProductCard(html) {
    try {
      // Parse HTML to find product card wrapper
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product card wrapper by class
      const productCard = doc.querySelector('.wp-product-card-view--js');

      if (productCard) {
        return productCard.outerHTML;
      }

      // Fallback: try to find any product card element
      const fallback =
        doc.querySelector('.wp-w-full') || doc.querySelector('[class*="product"]') || doc.body.firstElementChild;

      return fallback ? fallback.outerHTML : null;
    } catch (error) {
      console.warn('Error extracting product card:', error);
      return null;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  window.recentlyViewedProducts = new RecentlyViewedProducts();

  // Make clear function globally available
  window.clearRecentlyViewed = function () {
    window.recentlyViewedProducts.clearRecentlyViewed();
  };
});

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecentlyViewedProducts;
}
